/**
 * Triggers a file download from a given object URL, handling browser-specific behaviors.
 * Creates a hidden anchor element to initiate the download, sets the filename if supported,
 * and performs cleanup by removing the anchor and revoking the object URL.
 *
 * @param objectUrl - The object URL (e.g., created from a Blob) to download.
 * @param filename - The desired filename for the downloaded file.
 * @remarks
 * - In Firefox and Safari, the filename may not be set due to Content-Security-Policy restrictions.
 * - The object URL is revoked after a 2-second delay to avoid premature revocation issues.
 * @internal
 */
export function downloadFromUrl(objectUrl: string, filename: string) {
  const anchor = window.document.createElement('a');
  anchor.style.display = 'none';
  anchor.href = objectUrl;
  anchor.target = '_blank';

  const userAgent = window.navigator.userAgent;
  const isFirefox = userAgent.includes('Firefox');
  const isSafari =
    userAgent.includes('Safari') && !userAgent.includes('Chrome'); // Chrome user agents can also contain "Safari"

  // Set filename used when saving.
  if (!isFirefox && !isSafari) {
    // For Firefox, if this is added, the operation gets blocked by the
    // Content-Security-Policy (if it is cross origin e.g running inside
    // app engine frame. 'Uncaught Error: Access to ... from script denied.').
    // When we don't add it, a random name will be used in Firefox (but with correct
    // extension as given in blob type e.g. 'csv').
    anchor.download = filename;
  }
  window.document.body.appendChild(anchor);

  anchor.click();

  // Clean up (remove anchor and revoke object url).
  anchor.remove();
  // Note: For Firefox, calling revokeObjectURL immediately causes the operation
  // to get blocked by the Content-Security-Policy (if it is cross origin e.g running
  // inside app engine frame. 'Uncaught Error: Access to ... from script denied.').
  // Putting the revoke call directly in an onclick handler is
  // also too early, so we do it in a timeout so that the url is not revoked too early.
  setTimeout(() => URL.revokeObjectURL(objectUrl), 2000);
}

/**
 * Downloads the given blob using an object url using a temporary anchor element.
 *
 * @param blob - The blob to download
 * @param filename - The filename to use, for the downloaded blob. Note however, for
 * Firefox the given filename will be ignored and a random filename will be used. It
 * will have the correct extension however as per blob type e.g. 'csv'.
 * @internal
 */
export function downloadBlob(blob: Blob, filename: string): void {
  const objectUrl = URL.createObjectURL(blob);
  downloadFromUrl(objectUrl, filename);
}

/**
 * Downloads raw string data into CSV file
 *
 * @param csvString - separated with comma string to parse to a csv file
 * @param fileName - name for the created file
 * @internal
 */
export function downloadCSV(csvString: string, fileName: string): void {
  downloadBlob(
    new Blob([csvString], { type: 'text/csv;charset=utf8' }),
    fileName,
  );
}

/**
 * Escapes a value for CSV. Arrays will be stringified before escaping.
 * If the value contains double quotes, these are escaped by doubling them.
 * If the value contains CSV injection characters, these are escaped by prefixing the value with a single quote.
 * If the value contains special characters, these are escaped by enclosing the entire string in double quotes.
 *
 * @param original - original cell value
 *
 * @returns The escaped value. If the value is undefined or null, an empty string is returned.
 * @internal
 */
export function escapeForCSV(original: unknown): string {
  if (original === undefined || original === null) {
    return original === undefined ? 'undefined' : 'null';
  } else if (Array.isArray(original)) {
    return escapeForCSV(original.toString());
  } else if (typeof original === 'string') {
    // Escape double quotes by doubling them
    let escaped = original.replace(/"/g, '""');

    // Check for CSV injection characters (=, +, -, @, tabs, carriage returns)
    if (/^[=+\-@]/.test(escaped) || /^[\t\r]/.test(escaped)) {
      // Escape by prefixing with a single quote
      escaped = `'${escaped}`;
    }

    // Check if the value contains any special characters (commas, newlines, carriage returns, tabs, double quotes)
    if (/[,\n\r\t"]/.test(escaped)) {
      // Escape by enclosing the string in double quotes
      escaped = `"${escaped}"`;
    }

    return escaped;
  }

  return original as string;
}
